import axios from "axios";
import APP_CONFIG from "./configuration/AppConfig";
import { IHistoryTagsParams, ILttbHistoryTagsParams } from "../data_point/models/TagChartModel";
import {
  IStringToTimeValueDictionary,
  IStringToTimeValueListDictionary,
  IMultiSiteStringToTimeValueDictionary,
} from "../data_point/models/TagsModels";
import {
  TagQueryHistoryRequestDto,
  TagQueryHistoryResponseDto,
  TagQueryLatestRequestDto,
  TagQueryLatestResponseDto,
  MultiSiteTagQueryLatestResponseDto,
  TagQueryValueDto,
  TagSelectionDto,
} from "./TagQueryModels";
import { createFixedIntervalHistoryQuery, createLttbHistoryQuery } from "./TagQueryUtils";

export const adminClearTagsCacheSendAsync = async () => {
  const url = `${APP_CONFIG.serviceUrls.apiHostUrl}/Admin/clearTagsCache`;
  try {
    await axios.post(url);
  } catch (error) {
    console.error(error);
    throw new Error("SYSTEM_ERROR -> unable to clearCache", { cause: error });
  }
};

export async function queryTagsLatest(
  request: TagQueryLatestRequestDto,
  signal?: AbortSignal
): Promise<TagQueryLatestResponseDto> {
  const url = `${APP_CONFIG.serviceUrls.apiHostUrl}/tags/latest`;
  const response = signal
    ? await axios.post<TagQueryLatestResponseDto>(url, request, { signal })
    : await axios.post<TagQueryLatestResponseDto>(url, request);

  return response.data;
}

export async function queryMultiSiteTagsLatest(): Promise<MultiSiteTagQueryLatestResponseDto> {
  const url = `${APP_CONFIG.serviceUrls.apiHostUrl}/tags/latest/multisite`;
  const response = await axios.get<MultiSiteTagQueryLatestResponseDto>(url);

  return response.data;
}

export async function queryTagsHistory(
  request: TagQueryHistoryRequestDto
): Promise<TagQueryHistoryResponseDto> {
  const url = `${APP_CONFIG.serviceUrls.apiHostUrl}/tags/history`;
  const response = await axios.post<TagQueryHistoryResponseDto>(url, request);

  return response.data;
}

export const getTagsLatestAsync = async (
  siteId: string,
  selections: TagSelectionDto[],
  signal?: AbortSignal
): Promise<IStringToTimeValueDictionary> => {
  if (selections.length === 0) {
    throw new Error("At least one tag selection is required.");
  }
  const startTime = Date.now();
  const response = await queryTagsLatest({ siteId, selections }, signal);
  if (APP_CONFIG.isAnalysisApiActive) {
    const endTime = Date.now();
    const duration = endTime - startTime;
    console.log(`api - getTagsLatestAsync took ${duration}ms to respond.`);
  }
  return tagQueryResponseAdapter.latest(response);
};

export const getMultiSiteTagsLatest = async (): Promise<IMultiSiteStringToTimeValueDictionary> => {
  const response = await queryMultiSiteTagsLatest();
  return Object.fromEntries(
    Object.entries(response.sites).map(([siteId, latestTags]) => [
      siteId,
      tagQueryResponseAdapter.latest(latestTags),
    ])
  );
};

export const getTagsHistoryAsync = async (
  params: IHistoryTagsParams
): Promise<IStringToTimeValueListDictionary> => {
  const response = await queryTagsHistory({
    siteId: params.siteId,
    selections: params.selections,
    query: createFixedIntervalHistoryQuery(
      params.range.start.valueOf(),
      params.range.end.valueOf(),
      params.samplingIntervalInSeconds,
      "average"
    ),
  });
  return tagQueryResponseAdapter.history(response);
};

export const getTagsHistoryLttbAsync = async (
  params: ILttbHistoryTagsParams
): Promise<IStringToTimeValueListDictionary> => {
  const response = await queryTagsHistory({
    siteId: params.siteId,
    selections: params.selections,
    query: createLttbHistoryQuery(
      params.range.start.valueOf(),
      params.range.end.valueOf(),
      params.limit
    ),
  });
  return tagQueryResponseAdapter.history(response);
};

export const getTagsHistoryLatestAsync = async (
  params: IHistoryTagsParams
): Promise<IStringToTimeValueListDictionary> => {
  const response = await queryTagsHistory({
    siteId: params.siteId,
    selections: params.selections,
    query: createFixedIntervalHistoryQuery(
      params.range.start.valueOf(),
      params.range.end.valueOf(),
      params.samplingIntervalInSeconds,
      "last"
    ),
  });
  return tagQueryResponseAdapter.history(response);
};

export const tagQueryResponseAdapter = {
  latest(response: TagQueryLatestResponseDto): IStringToTimeValueDictionary {
    const output: IStringToTimeValueDictionary = {};

    for (const result of response.results) {
      for (const series of result.series) {
        // The flat latest dictionary has no representation for a requested series with no value.
        if (series.value === null) {
          continue;
        }

        output[toFlatTagName(result.logicalTagName, series.deviceId)] = toValueTime(series.value);
      }
    }

    return output;
  },

  history(response: TagQueryHistoryResponseDto): IStringToTimeValueListDictionary {
    const output: IStringToTimeValueListDictionary = {};

    for (const result of response.results) {
      for (const series of result.series) {
        output[toFlatTagName(result.logicalTagName, series.deviceId)] = series.values.map(toValueTime);
      }
    }

    return output;
  },
};

function toFlatTagName(logicalTagName: string, deviceId: number | null): string {
  return deviceId === null ? logicalTagName : `${logicalTagName}:${deviceId}`;
}

function toValueTime(value: TagQueryValueDto) {
  return { time: new Date(value.time), value: value.value };
}
